import React, { useContext, useEffect, useMemo, useCallback, useRef } from 'react';
import { Tab } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import _, { debounce } from 'lodash';
import { selectUser } from '../../../reducers/user.selectors';
import {
  getProductLoadingState as getProductLoadingStateSelector,
  getMindItemLoadingState as getMindItemLoadingStateSelector,
  getVideoMindItemsLoadingState as getVideoMindItemsLoadingStateSelector,
  SearchLoadingStatus,
  getPaginationSelector,
  getUnifiedPaginationSelector,
  getCourseMindItemsLoadingState,
  getUnifiedLoadingState,
  selectRelatedTopicsByParams,
  selectSearchFilters,
} from '../../../reducers/search.selectors';
import { zIndex } from '../../../constants';
import { useTheme } from '@mui/material/styles';
import MuTabsNewUI from '../../common/MuTabsNewUI';
import { SearchFilters, SearchResultsTab as SearchResultsTabEnum } from '../../../types/search';
import { MindItemSearchResult } from '@klab-berlin/api-sdk/lib/types/responses/MindItem';
import { palette } from '../../../assets/styles/themes/tokens';
import { Box, Divider } from '@mui/material';

import { MuiCardCommonProps } from '../../cards/MuiCard/MuiCardBase/MuiCardBase';
import { ProductSearchResult } from '@klab-berlin/api-sdk/lib/types/responses/Product';
import { ItemsTab } from '../../common/MuTabsNewUI/MuTabsNewUI';
import searchResultsStorageManager from '../../../utils/searchResultsStorageManager';
import { SearchCompleted, SearchCompletedProperties } from '../../../ampli';
import {
  getAmpliSearchEventPayload,
  getCurrentSearchId,
  startNewSearchJourney,
  SearchEventData
} from '../../../utils/ampli';
import services from '../../../services';
import SearchResultsTab from './SearchResultsTabs';
import { useNewUiSearchPagePadding } from '../../../routes/courses/CourseSearchPage/CourseSearchPage';
import useCardDimensions from '../../../utils/useCardDimensions';
import { Alert } from '../../NotificationAlert/NotificationAlert';
import { MasterLayoutContext } from '../../../state/MasterLayoutProvider';

export type SearchResultsItemsTab = ItemsTab<SearchResultsTabEnum> & { loadMore: () => void };

interface SearchResultsProps {
  currentTab: SearchResultsTabEnum;
  requestFilters: SearchFilters;
  trackingPayload: SearchEventData;
  tabs: SearchResultsItemsTab[]
  setCurrentTab?: (tab: SearchResultsTabEnum) => void;
  searchTerm?: string;
  forceNewDesign?: boolean;
  onShowNotification?: (alert: Alert) => void;
  onRelatedTopicSelect?: (topic: string) => void;
  isRelatedTopicsTreatment?: boolean;
}

const mapCurrentTabToSearchStateKey = (currentTab: SearchResultsTabEnum) => {
  switch (currentTab) {
    case SearchResultsTabEnum.MINDITEMS:
      return 'mindItems';
    case SearchResultsTabEnum.PRODUCTS:
      return 'products';
    case SearchResultsTabEnum.VIDEOS:
      return 'videoMindItems';
    case SearchResultsTabEnum.COURSES:
      return 'courseMindItems';
    case SearchResultsTabEnum.ALL:
      return 'all';
  }
};

/**
 * SEAR-460: `useAiFilters` gates which tag source the card renders for
 * subject/schoolType/classYear so it matches the filter values the user picked:
 * - `true`  → AI tags only (CP sources omitted)
 * - `false` → CP tags only (AI sources omitted)
 * - `undefined` (dashboard carousels, courses — no search cohort) → both are
 *   passed and the card falls back AI-first-then-CP, i.e. today's behavior.
 * `aiMainTopic` is not one of the three switched fields and is always passed.
 *
 * CP `schoolTypes` is only populated in explicit control mode (`false`): the
 * card had no CP school-type source before SEAR-460, so passing it for the
 * `undefined` (dashboard/course) callers would surface a school-type chip they
 * never rendered. In AI mode the AI school type is used; in legacy mode it stays
 * AI-only, exactly as today.
 */
export const getCommonCardProps = (
  item: MindItemSearchResult | ProductSearchResult,
  useAiFilters?: boolean,
): MuiCardCommonProps => {
  const includeCp = useAiFilters !== true;
  const includeAi = useAiFilters !== false;
  const includeCpSchoolType = useAiFilters === false;
  return {
    id: item.id,
    publisher: item.publisher || '',
    description: item.description || '',
    subjects: includeCp ? item.subjects : undefined,
    schoolTypes: includeCpSchoolType ? item.schoolTypes : undefined,
    cover: item.thumbnail,
    title: item.title,
    classYears: includeCp ? item.classYears : undefined,
    isNew: item.isNew,
    date: item.publisherReleaseDate,
    isReadOnly: item.isReadOnly,
    isPinned: item.isPinned,
    itemData: item,
    aiMainTopic: item.aiTags?.aiMainTopic || undefined,
    aiProductItemSubject: includeAi ? item.aiTags?.aiProductItemSubject || undefined : undefined,
    aiProductItemClassYears: includeAi ? item.aiTags?.aiProductItemClassYears || undefined : undefined,
    aiProductItemSchoolType: includeAi ? item.aiTags?.aiProductItemSchoolType || undefined : undefined,
  };
};

const debounceTime = 500;
const searchEngineVersion = 2;

const SearchResults: React.FC<SearchResultsProps> = (props) => {
  const theme = useTheme();
  const cardDimensions = useCardDimensions();
  const { mainContentContainer } = useContext(MasterLayoutContext);

  const mindItemsPagination = useSelector(getPaginationSelector('mindItems'));
  const videoMindItemsPagination = useSelector(getPaginationSelector('videoMindItems'));
  const courseMindItemsPagination = useSelector(getPaginationSelector('courseMindItems'));
  const productsPagination = useSelector(getPaginationSelector('products'));
  const unifiedPagination = useSelector(getUnifiedPaginationSelector);

  const mindItemLoadingState = useSelector(getMindItemLoadingStateSelector);
  const videoMindItemsLoadingState = useSelector(getVideoMindItemsLoadingStateSelector);
  const productLoadingState = useSelector(getProductLoadingStateSelector);
  const courseMindItemsLoadingState = useSelector(getCourseMindItemsLoadingState);
  const unifiedLoadingState = useSelector(getUnifiedLoadingState);
  // Dedupe SearchCompleted by (searchId, tab) so transient INITIAL→LOADED cycles
  // (filter churn, overlapping dispatches) can't refire the event for the same journey.
  const firedSearchCompletedKeysRef = useRef<Set<string>>(new Set());

  // Mirror RelatedTopicsPills' visibility: topics are only fetched in the
  // treatment, where the cache resolves to an array (true when chips render,
  // false when it resolved empty). Control never shows the block, so it always
  // reports false.
  const searchFilters = useSelector(selectSearchFilters);
  const relatedTopicsParams = props.searchTerm
    ? {
      term: props.searchTerm,
      subject: searchFilters?.subject,
      classYear: searchFilters?.classYear,
      schoolType: searchFilters?.schoolType,
    }
    : undefined;
  const relatedTopics = useSelector(selectRelatedTopicsByParams(relatedTopicsParams));
  const relatedTopicsDisplayed = props.isRelatedTopicsTreatment
    ? Array.isArray(relatedTopics) && relatedTopics.length > 0
    : false;

  const user = useSelector(selectUser);
  const userActionConfig = {
    hideCollectionAction: user?.hideCollectionAction,
    hidePinAction: user?.hidePinAction,
  };

  const mapTabToPagination = useMemo(() => ({
    [SearchResultsTabEnum.MINDITEMS]: mindItemsPagination,
    [SearchResultsTabEnum.PRODUCTS]: productsPagination,
    [SearchResultsTabEnum.VIDEOS]: videoMindItemsPagination,
    [SearchResultsTabEnum.COURSES]: courseMindItemsPagination,
    [SearchResultsTabEnum.ALL]: unifiedPagination,
  }), [
    mindItemsPagination, 
    productsPagination, 
    videoMindItemsPagination, 
    courseMindItemsPagination, 
    unifiedPagination
  ]);

  const mapTabToEventName:{[key: string]: 'document' | 'product' | 'video' | 'course'} = useMemo(() => ({
    [SearchResultsTabEnum.MINDITEMS]: 'document',
    [SearchResultsTabEnum.PRODUCTS]: 'product',
    [SearchResultsTabEnum.VIDEOS]: 'video',
    [SearchResultsTabEnum.COURSES]: 'course',
    [SearchResultsTabEnum.ALL]: 'document',
  }), []);

  const mapTabToLoadingState = useMemo(() => ({
    [SearchResultsTabEnum.MINDITEMS]: mindItemLoadingState,
    [SearchResultsTabEnum.PRODUCTS]: productLoadingState,
    [SearchResultsTabEnum.VIDEOS]: videoMindItemsLoadingState,
    [SearchResultsTabEnum.COURSES]: courseMindItemsLoadingState,
    [SearchResultsTabEnum.ALL]: unifiedLoadingState
  }), [
    mindItemLoadingState,
    productLoadingState,
    videoMindItemsLoadingState,
    courseMindItemsLoadingState,
    unifiedLoadingState
  ]);

  const searchStateKey = mapCurrentTabToSearchStateKey(props.currentTab);
  const saveScrollProperties = useCallback((currentTab: SearchResultsTabEnum) => {
    const scrollY = mainContentContainer?.scrollTop || 0;
    const tabKey = mapCurrentTabToSearchStateKey(currentTab);
    const offset = mapTabToPagination[currentTab].offset;
    searchResultsStorageManager.setSearchStateInstance(
      tabKey,
      offset,
      scrollY
    );
  }, [mainContentContainer, mapTabToPagination]);

  useEffect(() => {
    const resetStorageManager = () => {
      searchResultsStorageManager.clearSearchState();
    };

    window.addEventListener('beforeunload', resetStorageManager);

    return () => {
      window.removeEventListener('beforeunload', resetStorageManager);
    };
  }, []);

  // Restore scroll position
  useEffect(() => {
    if (!mainContentContainer) return;

    const searchStateInstance = searchResultsStorageManager.getSearchStateInstance(searchStateKey);
    if (!_.isEmpty(searchStateInstance)) {
      const { scrollPosition } = searchStateInstance;
      mainContentContainer.scrollTo(0, scrollPosition);
    } else {
      mainContentContainer.scrollTo(0, 0);
    }
  }, [props.currentTab, mainContentContainer, searchStateKey]);

  // Track scroll position
  useEffect(() => {
    if (!mainContentContainer) return;

    const handleScroll = debounce(() => {
      saveScrollProperties(props.currentTab);
    }, debounceTime);

    mainContentContainer.addEventListener('scroll', handleScroll);

    return () => {
      handleScroll.cancel();
      mainContentContainer.removeEventListener('scroll', handleScroll);
    };
  }, [props.currentTab, mainContentContainer, saveScrollProperties]);

  useEffect(() => {
    // SearchPage now fires only the active tab's request (SEAR-500), so SearchCompleted fires
    // per tab when *that* tab finishes loading. The payload reports the current tab's count;
    // other tabs' counts are populated whenever the user visits them.
    const currentTabLoadingState = mapTabToLoadingState[props.currentTab];

    if (currentTabLoadingState !== SearchLoadingStatus.LOADED) return;

    // Related topics fetch independently and can resolve *after* results load, so
    // firing now would record 'related topics displayed' from a stale (undefined)
    // cache. In the treatment, defer until that term's fetch settles — it resolves
    // to [] even on failure, so undefined reliably means still in flight. The
    // re-run on `relatedTopicsDisplayed` fires the event once topics settle.
    if (props.isRelatedTopicsTreatment && props.searchTerm && relatedTopics === undefined) return;

    // If no searchId exists (e.g., user navigated directly to search page without submitting a search),
    // create one now so it persists throughout the search journey
    let searchId = getCurrentSearchId();
    if (!searchId) {
      searchId = startNewSearchJourney();
    }

    const firedKey = `${searchId}:${props.currentTab}`;
    if (firedSearchCompletedKeysRef.current.has(firedKey)) return;
    firedSearchCompletedKeysRef.current.add(firedKey);

    // Build a typed payload so the SearchCompleted constructor still validates the
    // surrounding fields. The ALL tab maps to `'document'` which isn't a key in the
    // ampli schema — we deliberately omit a results count there rather than emit an
    // unknown key. Per-tab events still fire when the user visits a concrete tab.
    const total = mapTabToPagination[props.currentTab].total;
    const searchResultsPayload: Partial<SearchCompletedProperties> = {};
    switch (props.currentTab) {
      case SearchResultsTabEnum.MINDITEMS:
        searchResultsPayload['nr of minditem search results'] = total;
        break;
      case SearchResultsTabEnum.PRODUCTS:
        searchResultsPayload['nr of product search results'] = total;
        break;
      case SearchResultsTabEnum.VIDEOS:
        searchResultsPayload['nr of video search results'] = total;
        break;
      case SearchResultsTabEnum.COURSES:
        searchResultsPayload['nr of course search results'] = total;
        break;
    }
    const ampliSearchEventPayload = getAmpliSearchEventPayload(props.trackingPayload);

    const searchSuggestionApplied = searchResultsStorageManager.getSearchSuggestionApplied();
    const relatedTopicsApplied = searchResultsStorageManager.getRelatedTopicsApplied();

    const event = new SearchCompleted({
      // The COURSES → 'course' mapping above can't be reached from SearchPage (no
      // courses tab is registered there), but `mapTabToEventName`'s static type still
      // includes it, so we narrow at the call site.
      'search results tab': mapTabToEventName[props.currentTab] as
        SearchCompletedProperties['search results tab'],
      ...searchResultsPayload,
      'search term': ampliSearchEventPayload['search term'],
      'search filter': ampliSearchEventPayload['search filter'] ?? [],
      'app section': ampliSearchEventPayload['app section'],
      'search algorithm': ampliSearchEventPayload['search algorithm'],
      'search id': searchId,
      ...(searchSuggestionApplied != null && { 'search suggestion applied': searchSuggestionApplied }),
      ...(relatedTopicsApplied != null && { 'related topics applied': relatedTopicsApplied }),
      'related topics displayed': relatedTopicsDisplayed,
    });

    services.track.ampliEventTrack({ event });
  }, [
    mapTabToEventName,
    mapTabToPagination,
    mapTabToLoadingState,
    props.currentTab,
    props.trackingPayload,
    props.searchTerm,
    props.isRelatedTopicsTreatment,
    relatedTopics,
    relatedTopicsDisplayed,
  ]);

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    props.setCurrentTab && props.setCurrentTab(props.tabs[newValue].key);
  };

  const newUiSearchPagePadding = useNewUiSearchPagePadding();

  const searchResultsTabContainerSx = {
    position: 'sticky',
    zIndex: zIndex.searchResultsTab,
    background: palette.background.paperElevation1,
    top: 0,
  };

  return (
    <Box 
      data-testid='new-ui-search-results'
      sx={{ 
        paddingX: 0,
        fontSize: '16px',
        [theme.breakpoints.down('mobile')]: {
          fontSize: '12px',
        },
      }}
    >
      <Box>
        <Tab.Container
          activeKey={props.currentTab}
          onSelect={(key) => props.setCurrentTab && props.setCurrentTab(key as SearchResultsTabEnum)}
        >
          <Box
            sx={searchResultsTabContainerSx}
          >
            <Box sx={{
              maxWidth: cardDimensions.desktop.maxWidth,
              marginX: 'auto',
              paddingX: 0,
            }}>
              <MuTabsNewUI tabs={props.tabs} handleChange={handleChange} value={props.currentTab} />
            </Box>
          </Box>
          {props.tabs.length > 1 && 
            <Divider sx={{ 
              position: 'relative',
            }} />
          }
          <Box 
            sx={{
              maxWidth: cardDimensions.desktop.maxWidth,
              marginX: 'auto',
              paddingX: newUiSearchPagePadding,
            }}>
            <Box sx={{ marginX: '2px' }}>
              <Tab.Content>
                {props.tabs.map((tab) =>
                  <SearchResultsTab
                    key={tab.key}
                    searchResultsTab={tab.key}
                    searchTerm={props.searchTerm}
                    requestFilters={props.requestFilters}
                    searchEngineVersion={searchEngineVersion}
                    loadMore={tab.loadMore}
                    userActionConfig={userActionConfig}
                    currentTab={props.currentTab}
                    onShowNotification={props.onShowNotification}
                    onRelatedTopicSelect={props.onRelatedTopicSelect}
                  />
                )}
              </Tab.Content>
            </Box>
          </Box>
        </Tab.Container>
      </Box>
    </Box>
  );
};

export default SearchResults;
